<template>
  <section class="editor-toolbar">
    <div class="toolbar-btn-group">
      <label class="toolbar-btn toolbar-add-img clickable">
        <input
          ref="imageInputRef"
          type="file"
          class="no-display"
          :accept="acceptableImageTypeString"
          v-on:click="resetImageInput"
          v-on:change="onImageChange">
      </label>

      <button class="toolbar-btn toolbar-add-video clickable"
              v-on:click="addVideo"></button>

      <label class="toolbar-btn toolbar-add-audio clickable">
        <input
          type="file"
          class="no-display"
          :accept="acceptableAudioTypeString"
          @change="onAudioChange">
      </label>

      <button class="toolbar-btn toolbar-add-math clickable"
              v-on:click="addMath"></button>

      <!-- table not included
      <button class="toolbar-btn toolbar-add-table clickable"
              v-on:click="addTable"></button>
      -->
    </div>

    <div class="toolbar-btn-group">
      <button class="toolbar-btn toolbar-bold-text clickable"
              v-on:click="boldText"></button>

      <button class="toolbar-btn toolbar-italic-text clickable"
              v-on:click="italicText"></button>

      <button class="toolbar-btn toolbar-underline-text clickable"
              v-on:click="underlineText"></button>
    </div>

    <div class="toolbar-btn-group">
      <button class="toolbar-btn toolbar-add-ul clickable"
              v-on:click="addUl"></button>

      <button class="toolbar-btn toolbar-add-ol clickable"
              v-on:click="addOl"></button>
    </div>
  </section>
</template>

<script>
  import { mapGetters } from 'vuex';
  import { includes } from 'lodash';

  export default {
    name: 'editor-toobar',

    data() {
      return {
        acceptableImageTypes: [
          'image/jpg',
          'image/jpeg',
          'image/png',
        ],
        acceptableAudioTypes: [
          'audio/x-mpeg3',
          'audio/x-flac',
          'audio/mpeg3',
          'audio/x-mp3',
          'audio/x-m4a',
          'audio/x-ogg',
          'audio/x-wav',
          'audio/flac',
          'audio/mpeg',
          'audio/wave',
          'audio/m4a',
          'audio/mp4',
          'audio/mp3',
          'audio/wav',
          'audio/ogg',
        ],

        imageSizeLimit: 10 * 1024 * 1024,
        audioSizeLimit: 20 * 1024 * 1024,
      };
    },

    computed: {
      ...mapGetters([
        'activeEditorLocation',
        'activeEditorId',
      ]),

      acceptableImageTypeString() {
        return this.acceptableImageTypes.join(',');
      },

      acceptableAudioTypeString() {
        return this.acceptableAudioTypes.join(',');
      },
    },

    methods: {
      getDefaultPlaceholders() {
        return [
          `<p>${this.$t('page_editor.non_quiz.placeholder')}</p>`,
          `<p>${this.$t('page_editor.question.placeholder')}</p>`,
          `<p>${this.$t('page_editor.choices.placeholder')}</p>`,
          `<p>${this.$t('page_editor.text_entry.placeholder')}</p>`,
          `<p>${this.$t('page_editor.description.placeholder')}</p>`,
        ];
      },

      resetImageInput() {
        this.$refs.imageInputRef.value = '';
      },

      onImageChange(event) {
        const images = event.target.files || event.dataTransfer.files;

        if (!images.length) {
          return null;
        }

        const file = images[0];

        if (!file || file.size === undefined || file.type === undefined) {
          return null;
        }

        // TODO validate image size
        if (file.size > this.imageSizeLimit) {
          // alert file size
          return this.$root.$emit('show::modal', 'file-size-alert');
        }

        if (this.acceptableImageTypes.indexOf(file.type) === -1) {
          // alert wrong file type
          return this.$root.$emit('show::modal', 'file-type-alert');
        }

        return this.createMedia(file, 'image');
      },

      onAudioChange(event) {
        const audios = event.target.files || event.dataTransfer.files;

        if (!audios.length) {
          return null;
        }

        const file = audios[0];

        if (!file || file.size === undefined || file.type === undefined) {
          return null;
        }

        if (file.size > this.audioSizeLimit) {
          // alert file size
          return this.$root.$emit('show::modal', 'audio-size-alert');
        }

        if (this.acceptableAudioTypes.indexOf(file.type) === -1) {
          // alert wrong file type
          return this.$root.$emit('show::modal', 'audio-type-alert');
        }

        return this.createMedia(file, 'audio');
      },

      onImageLoaded(event, file) {
        const dataImgId = Math.floor(Math.random() * 1000);
        const activeEditor = this.activeEditorId
          ? tinymce.get(this.activeEditorId)
          : tinymce.activeEditor;
        if (this.activeEditorLocation) {
          activeEditor.selection.setRng(this.activeEditorLocation);
        }

        if (activeEditor && activeEditor.getContent().indexOf('<img') !== -1) {
          return this.$root.$emit('show::modal', 'max-image-limit-alert');
        }

        const imgHtml = `<figure><img data-image-id="${dataImgId}" src="${event.target.result}"></figure>`;

        if (includes(this.getDefaultPlaceholders(), activeEditor.getContent())) {
          activeEditor.setContent(imgHtml);
        } else {
          activeEditor.insertContent(imgHtml);
        }

        return this.$emit('image-added', {
          in_content: activeEditor.id.indexOf('quiz-editor-') !== -1,
          key: dataImgId,
          image_file: file,
          image_url: event.target.result,
        });
      },

      onAudioLoaded(event, file) {
        return this.$emit('audio-added', {
          audio_file: file,
          audio_url: event.target.result,
        });
      },

      createMedia(file, mediaType) {
        if (!window.FileReader) {
          return this.$root.$emit('show::modal', 'file-reader-unsupported-alert');
        }

        const reader = new window.FileReader();

        reader.onload = (event) => {
          switch (mediaType) {
            case 'image':
              return this.onImageLoaded(event, file);
            case 'audio':
              return this.onAudioLoaded(event, file);
            default:
              return null;
          }
        };

        return reader.readAsDataURL(file);
      },

      addVideo() {
        let activeEditor;

        if (this.activeEditorId) {
          activeEditor = tinymce.get(this.activeEditorId);

          if (this.activeEditorLocation) {
            activeEditor.selection.setRng(this.activeEditorLocation);
          }
        } else {
          activeEditor = tinymce.activeEditor;
        }

        if (activeEditor && activeEditor.getContent().indexOf('<iframe') !== -1) {
          return this.$root.$emit('show::modal', 'max-video-limit-alert');
        }

        return this.$emit('show-add-video-modal');
      },

      addMath() {
        this.$root.$emit('show::modal', 'math-editor-modal');
      },

      // addTable() {
      //   tinymce.EditorManager.execCommand('mceInsertTable', true);
      // },

      boldText() {
        tinymce.EditorManager.execCommand('Bold', true);
      },

      italicText() {
        tinymce.EditorManager.execCommand('Italic', true);
      },

      underlineText() {
        tinymce.EditorManager.execCommand('Underline', true);
      },

      addUl() {
        tinymce.EditorManager.execCommand('InsertUnorderedList', true);
      },

      addOl() {
        tinymce.EditorManager.execCommand('InsertOrderedList', true);
      },
    },
  };
</script>



// WEBPACK FOOTER //
// src/components/EditorToolbar.vue